/**
 * Shared visual language for the Finance module.
 *
 * Before this file, every finance page (FinanceDashboard, FinanceJobs,
 * CustomerInvoices, SupplierInvoices, ...) defined its own near-identical
 * copy of COLORS/cardSx/SectionHeader/metric-card locally — 15 separate
 * copies that had already started to drift from each other (e.g. one
 * blueLight was #EFF6FF, the rest were #EFF8FF). That's why the module
 * read as inconsistent page to page. Importing from here instead of
 * redefining locally is what keeps every page in sync going forward.
 *
 * Görsel dil, TMS Control Tower (Ocean / Air / Road) panelleriyle aynıdır:
 * düz beyaz kartlar, ince kenarlık, gölge yok, sade başlık.
 */
import React, { isValidElement } from "react";
import { Card, Tooltip, IconButton, Collapse } from "@mui/material";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowBack from "@mui/icons-material/ArrowBack";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { financeText } from "finance-language";

export const COLORS = {
    navy: "#0B1F3A",
    navySoft: "#12345B",
    blue: "#175CD3",
    blueLight: "#EFF8FF",
    green: "#027A48",
    greenLight: "#ECFDF3",
    red: "#B42318",
    redLight: "#FEF3F2",
    amber: "#B54708",
    amberLight: "#FFFAEB",
    purple: "#6941C6",
    purpleLight: "#F4F3FF",
    text: "#101828",
    subtext: "#667085",
    border: "#E4E7EC",
    cardBorder: "rgba(15, 23, 42, 0.12)",
    muted: "#F9FAFB",
    page: "#F5F7FA",
    white: "#FFFFFF",
};

// A generous, consistent spacing/radius scale. Cramped-looking cards were
// mostly a symptom of every page picking its own tight ad-hoc padding
// (p={2}, gap={1}, 12px radius...); using one scale everywhere fixes that
// by construction instead of needing a per-page eyeball pass.
export const SPACE = {
    cardPadding: 2.5, // MDBox p={} units (MUI spacing, 1 = 8px) -> 20px
    cardPaddingLg: 3, // 24px, for hero/feature cards
    sectionGap: 2.5, // vertical gap between stacked sections -> 20px
    rowGap: 2, // Grid container spacing -> 16px
    radiusCard: "10px",
    radiusTile: "8px",
    radiusPill: "6px",
};

export const cardSx = {
    borderRadius: SPACE.radiusCard,
    background: COLORS.white,
    border: `1px solid ${COLORS.cardBorder}`,
    boxShadow: "none",
    overflow: "hidden",
};

const TONES = {
    blue: { bg: COLORS.blueLight, color: COLORS.blue, border: "#B2DDFF" },
    green: { bg: COLORS.greenLight, color: COLORS.green, border: "#ABEFC6" },
    red: { bg: COLORS.redLight, color: COLORS.red, border: "#FECDCA" },
    amber: { bg: COLORS.amberLight, color: COLORS.amber, border: "#FEDF89" },
    purple: { bg: COLORS.purpleLight, color: COLORS.purple, border: "#D9D6FE" },
    navy: { bg: "#EEF2F6", color: COLORS.navy, border: "#CDD5DF" },
};

export const getTone = (tone = "blue") => TONES[tone] || TONES.blue;

/** Consistent card shell — use instead of a bare <Card sx={{...cardSx}}>. */
export const FinanceCard = ({ children, sx = {}, ...rest }) => (
    <Card sx={{ ...cardSx, height: "100%", ...sx }} {...rest}>
        {children}
    </Card>
);

/** Consistent title/subtitle/action header, used at the top of every card. */
export const FinanceSectionHeader = ({ title, subtitle, action, sx = {} }) => (
    <MDBox
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={2}
        gap={2}
        sx={sx}
    >
        <MDBox minWidth={0}>
            <MDTypography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{title}</MDTypography>
            {subtitle && (
                <MDTypography display="block" mt={0.25} sx={{ fontSize: 12, color: COLORS.subtext }}>
                    {subtitle}
                </MDTypography>
            )}
        </MDBox>
        {action && <MDBox flexShrink={0}>{action}</MDBox>}
    </MDBox>
);

/**
 * A single supporting metric — deliberately smaller/quieter than
 * FinanceHeroStat, for the secondary-numbers row under a page's headline
 * figure.
 */
export const FinanceKpiTile = ({ title, value, subtitle, icon, tone = "blue", onClick, footer }) => {
    const selected = getTone(tone);
    return (
        <FinanceCard
            onClick={onClick}
            sx={{
                cursor: onClick ? "pointer" : "default",
                transition: "border-color .15s ease, box-shadow .15s ease",
                "&:hover": onClick
                    ? { borderColor: selected.color, boxShadow: "0 4px 14px rgba(15,23,42,0.06)" }
                    : {},
            }}
        >
            <MDBox p={2} display="flex" flexDirection="column" gap={1.25}>
                <MDBox display="flex" alignItems="center" gap={1.25}>
                    <MDBox
                        width={36}
                        height={36}
                        borderRadius={SPACE.radiusTile}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        sx={{ color: selected.color, background: selected.bg, "& svg": { fontSize: "20px !important" } }}
                    >
                        {icon}
                    </MDBox>
                    <MDTypography sx={{ fontSize: 12.5, fontWeight: 600, color: COLORS.subtext, lineHeight: 1.3 }}>
                        {title}
                    </MDTypography>
                </MDBox>
                <MDBox>
                    {isValidElement(value) ? (
                        value
                    ) : (
                        <MDTypography sx={{ fontSize: 24, fontWeight: 700, color: COLORS.text, lineHeight: 1.15 }}>
                            {value}
                        </MDTypography>
                    )}
                    {subtitle && (
                        <MDTypography display="block" mt={0.5} sx={{ fontSize: 12, color: COLORS.subtext }}>
                            {subtitle}
                        </MDTypography>
                    )}
                    {footer && <MDBox mt={1}>{footer}</MDBox>}
                </MDBox>
            </MDBox>
        </FinanceCard>
    );
};

/**
 * The one big headline figure a page opens with, plus a small context row
 * underneath it (revenue/cost/whatever the page's supporting numbers are).
 * Meant to be paired next to a "needs attention"-style panel via a Grid.
 */
export const FinanceHeroStat = ({ label, value, tone = "green", meta, context = [], icon, children }) => {
    const selected = getTone(tone);
    return (
        <FinanceCard>
            <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%" gap={2}>
                <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                    <MDBox minWidth={0}>
                        <MDTypography sx={{ fontSize: 12.5, fontWeight: 600, color: COLORS.subtext }}>{label}</MDTypography>
                        <MDTypography mt={0.75} sx={{ fontSize: 32, fontWeight: 700, color: COLORS.text, lineHeight: 1.05 }}>
                            {value}
                        </MDTypography>
                        {meta && (
                            <MDTypography display="block" mt={1} sx={{ fontSize: 12, color: COLORS.subtext }}>
                                {meta}
                            </MDTypography>
                        )}
                    </MDBox>
                    {icon && (
                        <MDBox
                            width={40}
                            height={40}
                            borderRadius={SPACE.radiusTile}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            sx={{ color: selected.color, background: selected.bg }}
                        >
                            {icon}
                        </MDBox>
                    )}
                </MDBox>

                {children}

                {context.length > 0 && (
                    <MDBox display="flex" gap={4} mt="auto" pt={2} sx={{ borderTop: `1px solid ${COLORS.border}` }}>
                        {context.map((item) => (
                            <MDBox key={item.label}>
                                <MDTypography display="block" sx={{ fontSize: 12, color: COLORS.subtext }}>
                                    {item.label}
                                </MDTypography>
                                <MDTypography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
                                    {item.value}
                                </MDTypography>
                            </MDBox>
                        ))}
                    </MDBox>
                )}
            </MDBox>
        </FinanceCard>
    );
};

/**
 * Zero-centered monthly bar chart (positive bars grow up from a baseline,
 * negative bars grow down from it), inside its own grounded container so
 * near-zero months read as "small", not as a missing/broken chart. `months`
 * is [{ key, label, value }].
 */
export const FinanceTrendChart = ({ months, formatValue, half = 46 }) => {
    if (!months || months.length < 2) return null;

    const maxPositive = Math.max(...months.map((m) => (m.value > 0 ? m.value : 0)), 1);
    const maxNegative = Math.max(...months.map((m) => (m.value < 0 ? -m.value : 0)), 1);

    return (
        <MDBox px={2} py={2} borderRadius={SPACE.radiusTile} sx={{ background: COLORS.muted, border: `1px solid ${COLORS.border}` }}>
            <MDBox display="flex" alignItems="stretch" gap={1.5}>
                {months.map((month, index) => {
                    const isLast = index === months.length - 1;
                    const isPositive = month.value >= 0;
                    const barPx = isPositive
                        ? Math.max((month.value / maxPositive) * half, month.value !== 0 ? 3 : 0)
                        : Math.max((-month.value / maxNegative) * half, 3);
                    const barColor = isLast
                        ? isPositive ? COLORS.green : COLORS.red
                        : isPositive ? "#D0D5DD" : "#FDA29B";

                    return (
                        <MDBox
                            key={month.key}
                            flexGrow={1}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            title={formatValue ? `${month.label}: ${formatValue(month.value)}` : month.label}
                        >
                            <MDBox height={`${half}px`} width="100%" display="flex" alignItems="flex-end" justifyContent="center">
                                {isPositive && (
                                    <MDBox sx={{ width: "60%", height: `${barPx}px`, minHeight: month.value !== 0 ? "3px" : 0, borderRadius: "3px 3px 0 0", background: barColor }} />
                                )}
                            </MDBox>
                            <MDBox width="100%" sx={{ borderTop: `1.5px solid ${COLORS.border}` }} />
                            <MDBox height={`${half}px`} width="100%" display="flex" alignItems="flex-start" justifyContent="center">
                                {!isPositive && (
                                    <MDBox sx={{ width: "60%", height: `${barPx}px`, borderRadius: "0 0 3px 3px", background: barColor }} />
                                )}
                            </MDBox>
                            <MDTypography
                                variant="caption"
                                mt={0.75}
                                sx={{ fontSize: "10px", color: isLast ? COLORS.text : COLORS.subtext, fontWeight: isLast ? 700 : 400 }}
                            >
                                {month.label.slice(0, 3)}
                            </MDTypography>
                        </MDBox>
                    );
                })}
            </MDBox>
        </MDBox>
    );
};

// Every page had its own mapping from a status string to a color — and they
// didn't all agree (e.g. "Overdue" was red on one page, amber on another).
// One canonical mapping, by normalized (lowercased) status text.
const STATUS_TONE_MAP = {
    draft: "navy",
    pending: "amber",
    "pending approval": "amber",
    partial: "amber",
    approved: "blue",
    issued: "blue",
    collected: "green",
    paid: "green",
    received: "green",
    overdue: "red",
    unpaid: "red",
    "not paid": "red",
    rejected: "red",
    cancelled: "red",
    credit: "purple",
    returned: "purple",
};

export const getStatusTone = (status) => STATUS_TONE_MAP[String(status || "").trim().toLowerCase()] || "navy";

/** Small pill for a status string, using the canonical color mapping above. */
export const FinanceStatusChip = ({ status, label }) => {
    const tone = getTone(getStatusTone(status));
    return (
        <MDBox
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                px: 1,
                py: 0.25,
                borderRadius: SPACE.radiusPill,
                fontSize: "11.5px",
                fontWeight: 600,
                lineHeight: 1.6,
                color: tone.color,
                background: tone.bg,
                whiteSpace: "nowrap",
            }}
        >
            <MDBox component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: tone.color, flexShrink: 0 }} />
            {label || status}
        </MDBox>
    );
};

/* ------------------------------------------------------------------ */
/* Sayfa başlığı (eski gradyanlı lacivert hero bandının yerine)        */
/* ------------------------------------------------------------------ */

/** EN / TR dil seçici; Control Tower'daki sekme seçicisiyle aynı görünüm. */
export const FinanceLanguageToggle = ({ value, onChange }) => (
    <MDBox
        display="inline-flex"
        sx={{ p: 0.5, gap: 0.5, borderRadius: "8px", background: COLORS.white, border: `1px solid ${COLORS.cardBorder}` }}
    >
        {[
            ["en", "English", "/flags/gb.svg", "EN"],
            ["tr", "Türkçe", "/flags/tr.svg", "TR"],
        ].map(([code, title, flag, label]) => {
            const selected = value === code;
            return (
                <MDBox
                    key={code}
                    component="button"
                    type="button"
                    title={title}
                    onClick={() => onChange(code)}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        height: 30,
                        px: 1.25,
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 12,
                        fontWeight: 700,
                        color: selected ? COLORS.white : COLORS.subtext,
                        background: selected ? COLORS.navy : "transparent",
                        "&:hover": selected ? {} : { background: "#F1F5F9", color: COLORS.text },
                    }}
                >
                    <MDBox
                        component="img"
                        src={flag}
                        alt={title}
                        sx={{ width: 18, height: 13, objectFit: "cover", borderRadius: "2px", display: "block" }}
                    />
                    {label}
                </MDBox>
            );
        })}
    </MDBox>
);

/** Sayfa başlığındaki ana / ikincil aksiyon düğmesi. */
export const FinanceActionButton = ({ icon, children, onClick, variant = "primary", disabled = false, title, type = "button" }) => {
    const primary = variant === "primary";
    return (
        <MDBox
            component="button"
            type={type}
            onClick={onClick}
            disabled={disabled}
            title={title}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                height: 40,
                px: 2,
                borderRadius: "8px",
                border: primary ? "none" : `1px solid ${COLORS.cardBorder}`,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: primary ? COLORS.white : COLORS.text,
                background: primary ? COLORS.navy : COLORS.white,
                "& svg": { fontSize: "18px !important" },
                "&:hover": disabled
                    ? {}
                    : primary
                        ? { background: COLORS.navySoft }
                        : { borderColor: COLORS.blue, color: COLORS.blue },
            }}
        >
            {icon}
            {children}
        </MDBox>
    );
};

/** Yenile gibi tek ikonlu, kenarlıklı aksiyon. */
export const FinanceIconAction = ({ title, onClick, children, disabled = false }) => (
    <Tooltip title={title}>
        <span>
            <IconButton
                onClick={onClick}
                disabled={disabled}
                sx={{
                    width: 40,
                    height: 40,
                    color: COLORS.text,
                    background: COLORS.white,
                    border: `1px solid ${COLORS.cardBorder}`,
                    borderRadius: "8px",
                    "&:hover": { borderColor: COLORS.blue, color: COLORS.blue, background: COLORS.white },
                }}
            >
                {children}
            </IconButton>
        </span>
    </Tooltip>
);

/**
 * Sade sayfa başlığı: solda başlık / açıklama / küçük bilgiler, sağda öne çıkan
 * rakam kartı ile dil seçici + aksiyonlar.
 *
 * highlight: { label, value, caption, icon, tone }
 * meta: ["12 records", "Updated 10:24", ...]
 * badges: durum etiketleri gibi olduğu gibi çizilecek düğümler
 * controls: sağ kolonda aksiyonların üstünde duran ek kontroller (ör. para birimi seçici)
 * onBack / backTitle: başlığın solunda geri düğmesi
 */
export const FinancePageHeader = ({
    title,
    subtitle,
    meta = [],
    badges = [],
    highlight,
    language,
    onLanguageChange,
    actions,
    controls,
    onBack,
    backTitle = "Back",
    children,
}) => {
    const tone = getTone(highlight?.tone || "navy");
    return (
        <MDBox
            display="flex"
            flexDirection={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", lg: "flex-end" }}
            gap={2.5}
            mb={2.5}
        >
            <MDBox minWidth={0} flex={1}>
                <MDBox display="flex" alignItems="center" gap={1}>
                    {onBack && (
                        <FinanceIconAction title={backTitle} onClick={onBack}>
                            <ArrowBack sx={{ fontSize: "20px !important" }} />
                        </FinanceIconAction>
                    )}
                    <MDTypography sx={{ fontSize: 22, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.2px", lineHeight: 1.25 }}>
                        {title}
                    </MDTypography>
                </MDBox>
                {subtitle && (
                    <MDTypography sx={{ fontSize: 13, color: COLORS.subtext, mt: 0.5, maxWidth: 760 }}>{subtitle}</MDTypography>
                )}
                {(meta.filter(Boolean).length > 0 || badges.length > 0) && (
                    <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={1} mt={1.25}>
                        {badges.map((badge, index) => (
                            <React.Fragment key={`badge-${index}`}>{badge}</React.Fragment>
                        ))}
                        {meta.filter(Boolean).map((item, index) => (
                            <MDBox
                                key={index}
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.6,
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: "6px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: COLORS.subtext,
                                    background: "#EEF2F6",
                                }}
                            >
                                {item}
                            </MDBox>
                        ))}
                    </MDBox>
                )}
                {children}
            </MDBox>

            <MDBox display="flex" flexDirection="column" alignItems={{ xs: "stretch", lg: "flex-end" }} gap={1.5} flexShrink={0}>
                {highlight && (
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        px={2}
                        py={1.5}
                        sx={{ ...cardSx, minWidth: { lg: 320 } }}
                    >
                        {highlight.icon && (
                            <MDBox
                                width={40}
                                height={40}
                                borderRadius={SPACE.radiusTile}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                                sx={{ color: tone.color, background: tone.bg, "& svg": { fontSize: "22px !important", color: `${tone.color} !important` } }}
                            >
                                {highlight.icon}
                            </MDBox>
                        )}
                        <MDBox minWidth={0}>
                            <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.subtext }}>{highlight.label}</MDTypography>
                            <MDTypography sx={{ fontSize: 24, fontWeight: 700, color: COLORS.text, lineHeight: 1.15 }}>
                                {highlight.value}
                            </MDTypography>
                            {highlight.caption && (
                                <MDTypography sx={{ fontSize: 11.5, color: COLORS.subtext }}>{highlight.caption}</MDTypography>
                            )}
                        </MDBox>
                    </MDBox>
                )}

                {controls}

                <MDBox display="flex" alignItems="center" justifyContent={{ xs: "flex-start", lg: "flex-end" }} gap={1} flexWrap="wrap">
                    {onLanguageChange && <FinanceLanguageToggle value={language} onChange={onLanguageChange} />}
                    {actions}
                </MDBox>
            </MDBox>
        </MDBox>
    );
};

/**
 * Varsayılan olarak kapalı "Detaylı analiz" bölümü: sayfada yalnızca temel bilgiler görünür,
 * grafikler / kırılımlar tek tıkla açılır. Açık-kapalı tercihi sayfa bazında tarayıcıda hatırlanır.
 */
export const FinanceDetailsSection = ({ title, subtitle, storageKey, children, showHideLabels = [financeText("Show analysis"), financeText("Hide analysis")] }) => {
    const key = storageKey ? `finance.details.${storageKey}` : null;
    const [open, setOpen] = React.useState(() => {
        try {
            return key ? localStorage.getItem(key) === "1" : false;
        } catch {
            return false;
        }
    });
    const toggle = () =>
        setOpen((prev) => {
            const next = !prev;
            try {
                if (key) localStorage.setItem(key, next ? "1" : "0");
            } catch {
                // depolama kapalıysa yalnızca bu oturumda hatırlanır
            }
            return next;
        });

    return (
        <MDBox mt={SPACE.sectionGap}>
            {/* Tıklanabilir olduğu belli olsun: mavi zemin, sol vurgu çizgisi ve açık bir "göster" düğmesi */}
            <MDBox
                component="button"
                type="button"
                onClick={toggle}
                aria-expanded={open}
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: 2.5,
                    py: 2,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                    borderRadius: SPACE.radiusCard,
                    border: `1px solid ${open ? COLORS.blue : "#B2DDFF"}`,
                    borderLeft: `4px solid ${COLORS.blue}`,
                    background: open ? COLORS.white : COLORS.blueLight,
                    transition: "background .15s ease, border-color .15s ease, box-shadow .15s ease",
                    "&:hover": {
                        borderColor: COLORS.blue,
                        boxShadow: "0 4px 14px rgba(23, 92, 211, 0.12)",
                        "& .details-cta": { background: COLORS.navySoft },
                    },
                    "&:focus-visible": { outline: `2px solid ${COLORS.blue}`, outlineOffset: 2 },
                }}
            >
                <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                    <MDBox
                        width={40}
                        height={40}
                        borderRadius={SPACE.radiusTile}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        sx={{ color: COLORS.white, background: COLORS.blue }}
                    >
                        <InsightsOutlined sx={{ fontSize: "22px !important" }} />
                    </MDBox>
                    <MDBox minWidth={0}>
                        <MDTypography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{title}</MDTypography>
                        {subtitle && <MDTypography sx={{ fontSize: 12.5, color: COLORS.subtext }}>{subtitle}</MDTypography>}
                    </MDBox>
                </MDBox>
                <MDBox
                    className="details-cta"
                    component="span"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexShrink: 0,
                        px: 1.75,
                        py: 0.9,
                        borderRadius: "8px",
                        fontSize: 13,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        color: COLORS.white,
                        background: COLORS.navy,
                        transition: "background .15s ease",
                    }}
                >
                    {open ? showHideLabels[1] : showHideLabels[0]}
                    <ExpandMore
                        sx={{
                            fontSize: "20px !important",
                            transition: "transform .2s ease",
                            transform: open ? "rotate(180deg)" : "none",
                        }}
                    />
                </MDBox>
            </MDBox>
            <Collapse in={open} unmountOnExit>
                <MDBox display="flex" flexDirection="column" gap={SPACE.sectionGap} mt={SPACE.sectionGap}>
                    {children}
                </MDBox>
            </Collapse>
        </MDBox>
    );
};
